import { SITE_KEY } from "@/components/Recaptcha";

/**
 * Check if reCAPTCHA should be skipped (for testing environments)
 */
export function shouldSkipRecaptcha(): boolean {
  return process.env.NEXT_PUBLIC_SKIP_RECAPTCHA === "true";
}

/**
 * Check if reCAPTCHA is available in the window object
 */
export function isRecaptchaAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.grecaptcha !== "undefined";
}

/**
 * Wait for reCAPTCHA to be available
 * @param timeout - Maximum time to wait in milliseconds (default: 5000)
 * @returns Promise that resolves to true if reCAPTCHA is available, false if timeout
 */
export async function waitForRecaptcha(timeout: number = 5000): Promise<boolean> {
  if (shouldSkipRecaptcha()) {
    return true;
  }

  if (isRecaptchaAvailable()) {
    return true;
  }

  const startTime = Date.now();

  return new Promise((resolve) => {
    const checkInterval = setInterval(() => {
      if (isRecaptchaAvailable()) {
        clearInterval(checkInterval);
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        resolve(false);
      }
    }, 100);
  });
}

/**
 * Execute reCAPTCHA with the given action
 * @param action - The action name for reCAPTCHA
 * @returns Promise that resolves to the reCAPTCHA token or null if skipped/failed
 */
export async function executeRecaptcha(action: string): Promise<string | null> {
  // Skip reCAPTCHA in test environments
  if (shouldSkipRecaptcha()) {
    return "skipped";
  }

  // Wait for reCAPTCHA to be available
  const isAvailable = await waitForRecaptcha();

  if (!isAvailable) {
    console.warn("reCAPTCHA not available after timeout");
    return "timed out";
  }

  try {
    return await new Promise<string>((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(SITE_KEY, { action }).then(resolve, reject);
      });
    });
  } catch (error) {
    console.error("Error executing reCAPTCHA:", error);
    return null;
  }
}

/**
 * Execute reCAPTCHA and handle errors gracefully
 * @param action - The action name for reCAPTCHA
 * @param onSuccess - Callback with the reCAPTCHA token (or null if skipped)
 * @param onError - Optional error callback
 */
export async function executeRecaptchaWithCallback(
  action: string,
  onSuccess: (token: string | null) => void | Promise<void>,
  onError?: (error: Error) => void,
): Promise<void> {
  try {
    const token = await executeRecaptcha(action);
    await onSuccess(token);
  } catch (error) {
    if (onError) {
      onError(error as Error);
    } else {
      console.error("reCAPTCHA execution failed:", error);
    }
  }
}
